import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Text, TextField, Checkbox, Button, Flex } from '@chatsdet/ui';
import {
  isCustomStepMissingUrl,
  type ApiSequenceStep,
  type ApiSequenceConfig,
  type ApiCatalogListItem,
} from '@chatsdet/types';
import { ArrowLeft, Plus } from '@phosphor-icons/react';
import { SortableStepCard } from './SortableStepCard';
import { ApiSequenceGeneratePanel } from './ApiSequenceGeneratePanel';
import { ApiSequenceExportsEditor } from './ApiSequenceExportsEditor';
import { useWorkflowVariablesContext } from '../../pages/FlowDesigner/context/WorkflowVariablesContext';
import { useFlowOverlayPortalHost } from '../../pages/FlowDesigner/context/FlowOverlayPortalContext';
import { useCredentialAndPersonaOptions } from './CredentialPersonaPicker';

/** Generate a short random ID for internal tracking (not persisted). */
function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

type ApiSequenceEditorProps = {
  isOpen: boolean;
  config: ApiSequenceConfig;
  apiRequests: ApiCatalogListItem[];
  propertyId?: string;
  nodeId?: string;
  onSave: (config: ApiSequenceConfig) => void;
  onCancel: () => void;
};

/**
 * Full-screen editor overlay for API Sequence configuration.
 * Provides drag-and-drop reordering, expanded field layouts, and better UX.
 */
export function ApiSequenceEditor({
  isOpen,
  config,
  apiRequests,
  propertyId,
  nodeId,
  onSave,
  onCancel,
}: ApiSequenceEditorProps) {
  // Local state for editing
  const [localConfig, setLocalConfig] = React.useState<ApiSequenceConfig>(config);
  const [expandedStepIds, setExpandedStepIds] = React.useState<Set<string>>(() => new Set());

  // Canvas column to portal into. Without it the overlay's `absolute inset-0`
  // resolves against whatever positioned ancestor it is mounted under — the
  // right sidebar, when opened from a node's Configure tab.
  const overlayHost = useFlowOverlayPortalHost();

  // Upstream workflow variables available to this node
  const allWorkflowVars = useWorkflowVariablesContext();
  // Personas (credential templates) + concrete credentials, derived once here
  // and shared by every step card (instead of each card calling the hook).
  // `propertyId` scopes the list to the flow's property; when it is undefined —
  // e.g. an api_sequence node in a login-less flow — the picker lists org-wide
  // credentials so one can still be attached.
  const credentialPickerOptions = useCredentialAndPersonaOptions(propertyId);
  const upstreamWorkflowVars = React.useMemo(() => {
    if (!nodeId) return [];
    // Find the step index for the current node
    const currentStepIndex =
      allWorkflowVars.find((v) => v.definedByNodeId === nodeId)?.definedByStepIndex ?? Infinity;
    // Filter to variables defined upstream of this node
    return allWorkflowVars.filter(
      (v) =>
        v.definedByNodeId !== null &&
        v.definedByNodeId !== nodeId &&
        v.definedByStepIndex !== null &&
        v.definedByStepIndex < currentStepIndex
    );
  }, [allWorkflowVars, nodeId]);

  // Stable internal IDs for React keys and dnd-kit — never derived from step.stepId
  const internalIdsRef = React.useRef<string[]>([]);

  // Initialize local state once when the editor opens.
  // The component unmounts when closed, so mount-time config is always fresh.
  // Subsequent config reference changes must NOT reset state (would collapse open sections).
  const initializedRef = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;
      setLocalConfig(config);
      const nextInternalIds = config.steps.map(() => generateId());
      internalIdsRef.current = nextInternalIds;
      setExpandedStepIds(new Set());
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, config]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = internalIdsRef.current;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));

    if (oldIndex !== -1 && newIndex !== -1) {
      const newSteps = [...localConfig.steps];
      const [removedStep] = newSteps.splice(oldIndex, 1);
      newSteps.splice(newIndex, 0, removedStep);

      const newIds = [...ids];
      const [removedId] = newIds.splice(oldIndex, 1);
      newIds.splice(newIndex, 0, removedId);
      internalIdsRef.current = newIds;

      setLocalConfig((prev) => ({ ...prev, steps: newSteps }));
    }
  };

  const addStep = () => {
    const newStep: ApiSequenceStep = {
      apiRequestId: '',
      credentialId: null,
      stepId: `step${localConfig.steps.length + 1}`,
    };
    const newInternalId = generateId();
    internalIdsRef.current = [...internalIdsRef.current, newInternalId];
    setExpandedStepIds((prev) => {
      const next = new Set(prev);
      next.add(newInternalId);
      return next;
    });
    setLocalConfig((prev) => ({ ...prev, steps: [...prev.steps, newStep] }));
  };

  const updateStep = (index: number, step: ApiSequenceStep) => {
    const newSteps = [...localConfig.steps];
    newSteps[index] = step;
    setLocalConfig((prev) => ({ ...prev, steps: newSteps }));
  };

  const removeStep = (index: number) => {
    const newIds = [...internalIdsRef.current];
    const [removedId] = newIds.splice(index, 1);
    internalIdsRef.current = newIds;
    if (removedId) {
      setExpandedStepIds((prev) => {
        if (!prev.has(removedId)) return prev;
        const next = new Set(prev);
        next.delete(removedId);
        return next;
      });
    }
    const newSteps = localConfig.steps.filter((_, i) => i !== index);
    setLocalConfig((prev) => ({ ...prev, steps: newSteps }));
  };

  const getAvailableVariables = (stepIndex: number): string[] => {
    return localConfig.steps
      .slice(0, stepIndex)
      .map((s, i) => s.stepId || `step${i + 1}`);
  };

  // Same rule as the server's STEP_CUSTOM_URL_REQUIRED. The legacy node-data
  // save path never reaches that validator, so this guard is the only one there.
  const customStepUrlErrors = localConfig.steps.map((step) =>
    isCustomStepMissingUrl(step) ? 'Custom API sequence steps must specify a URL template' : undefined
  );
  const hasInvalidStep = customStepUrlErrors.some(Boolean);

  const handleSave = () => {
    if (hasInvalidStep) {
      // Surface the error by expanding every offending step's advanced section.
      setExpandedStepIds((prev) => {
        const next = new Set(prev);
        customStepUrlErrors.forEach((error, index) => {
          if (error) next.add(internalIdsRef.current[index]);
        });
        return next;
      });
      return;
    }
    onSave(localConfig);
  };

  const handleApplyGeneratedConfig = React.useCallback((newConfig: ApiSequenceConfig) => {
    const nextInternalIds = newConfig.steps.map(() => generateId());
    internalIdsRef.current = nextInternalIds;
    setExpandedStepIds(new Set(nextInternalIds));
    setLocalConfig(newConfig);
  }, []);

  const toggleStepExpanded = React.useCallback((stepInternalId: string) => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepInternalId)) {
        next.delete(stepInternalId);
      } else {
        next.add(stepInternalId);
      }
      return next;
    });
  }, []);

  // Derive credentialId from the first step's credential (if any) for the generate panel
  const defaultCredentialId = localConfig.steps[0]?.credentialId ?? null;

  // Ensure internal IDs array stays in sync with steps length
  // (guards against undefined keys if steps are added outside the effect)
  while (internalIdsRef.current.length < localConfig.steps.length) {
    internalIdsRef.current.push(generateId());
  }
  if (internalIdsRef.current.length > localConfig.steps.length) {
    internalIdsRef.current = internalIdsRef.current.slice(0, localConfig.steps.length);
  }

  // Use stable internal IDs for sortable context
  const sortableIds = internalIdsRef.current;

  if (!isOpen) return null;

  const overlay = (
    <>
      {/* Background - slides up */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-50 bg-background flex flex-col"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          className="border-b border-border px-6 py-4 flex items-center justify-between shrink-0"
        >
          <Flex align="center" gap="sm">
            <Button variant="icon" size="sm" iconOnly onPress={onCancel} tooltip="Back">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <Text weight="semibold" size="lg">
                Configure API Sequence
              </Text>
              <Text size="sm" className="text-muted-foreground">
                Define a sequence of API calls to execute in order
              </Text>
            </div>
          </Flex>
          <Flex align="center" gap="md">
            {hasInvalidStep && (
              <Text size="sm" className="text-destructive">
                Add a URL to every custom step before saving
              </Text>
            )}
            <Flex gap="sm">
              <Button variant="outline" onPress={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onPress={handleSave}>
                Save
              </Button>
            </Flex>
          </Flex>
        </motion.div>

        {/* Content - scrollable */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.25 } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          className="flex-1 overflow-auto"
        >
          <div className="max-w-3xl mx-auto py-8 px-6 space-y-6">
            {/* AI Generate panel */}
            <ApiSequenceGeneratePanel
              propertyId={propertyId}
              currentConfig={localConfig.steps.length > 0 ? localConfig : null}
              credentialId={defaultCredentialId}
              onApplyConfig={handleApplyGeneratedConfig}
            />

            {/* Sequence Settings */}
            <div className="rounded-lg border border-border bg-card p-5 space-y-4">
              <Text size="sm" weight="semibold" className="text-muted-foreground">
                Settings
              </Text>

              <TextField
                label="Description"
                aria-label="Description"
                value={localConfig.description || ''}
                onChange={(value) =>
                  setLocalConfig((prev) => ({ ...prev, description: value || undefined }))
                }
                placeholder="Describe what this API sequence does"
                description="A human-readable description of this sequence's purpose"
              />

              <Checkbox
                label="Stop on failure"
                description="When enabled, the sequence stops immediately if any step fails"
                isSelected={localConfig.stopOnFailure !== false}
                onChange={(checked) =>
                  setLocalConfig((prev) => ({ ...prev, stopOnFailure: checked }))
                }
              />
            </div>

            {/* Steps Section */}
            <div className="space-y-4">
              <Flex align="center" justify="between">
                <div>
                  <Text weight="semibold">Steps</Text>
                  <Text size="sm" className="text-muted-foreground">
                    Drag to reorder. Each step can reference data from previous steps.
                  </Text>
                </div>
                <Button variant="outline" size="sm" onPress={addStep} iconLeft={<Plus size={14} />}>
                  Add Step
                </Button>
              </Flex>

              {localConfig.steps.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 py-10 text-center">
                  <Text size="sm" className="text-muted-foreground">
                    No steps configured. Click "Add Step" to add an API call.
                  </Text>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {localConfig.steps.map((step, index) => (
                        <SortableStepCard
                          key={internalIdsRef.current[index]}
                          sortableId={internalIdsRef.current[index]}
                          step={step}
                          index={index}
                          apiRequests={apiRequests}
                          credentialPickerOptions={credentialPickerOptions}
                          onUpdate={(s) => updateStep(index, s)}
                          onRemove={() => removeStep(index)}
                          isAdvancedOpen={expandedStepIds.has(internalIdsRef.current[index])}
                          onToggleAdvanced={() => toggleStepExpanded(internalIdsRef.current[index])}
                          urlTemplateError={customStepUrlErrors[index]}
                          availableVariables={getAvailableVariables(index)}
                          workflowVariables={upstreamWorkflowVars.map((v) => ({
                            name: v.name,
                            source: v.source,
                          }))}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* Variable Exports */}
            <div className="space-y-4">
              <div>
                <Text weight="semibold">Variable Exports</Text>
                <Text size="sm" className="text-muted-foreground">
                  Values exported from API responses for use in subsequent workflow steps
                </Text>
              </div>

              <ApiSequenceExportsEditor
                exports={localConfig.exports || []}
                onChange={(exports) =>
                  setLocalConfig((prev) => ({
                    ...prev,
                    exports: exports.length > 0 ? exports : undefined,
                  }))
                }
                availableStepIds={localConfig.steps.map((s, i) => s.stepId || `step${i + 1}`)}
              />
            </div>

            {/* Help text */}
            <div className="rounded-md bg-muted/30 px-4 py-3">
              <Text size="xs" className="text-muted-foreground">
                <strong>Tip:</strong> Use template variables like{' '}
                <code className="px-1 py-0.5 rounded bg-muted font-mono text-[11px]">
                  {'{{step1.response.data.id}}'}
                </code>{' '}
                to pass data between steps. The response from each step is available to all
                subsequent steps.
              </Text>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </>
  );

  // Portal into the canvas column so the overlay covers the canvas rather than
  // the panel it was opened from. Falls back to rendering in place outside a
  // Flow Designer.
  return overlayHost ? createPortal(overlay, overlayHost) : overlay;
}
